"use client";

import { useEffect, useState } from "react";

import { AdvancedTradingViewChartContainer } from "@/components/app/chart/advanced-trading-view-chart-container";
import { PostLaunchOverviewTokenMetrics } from "@/components/app/profile/token-post-launch-overview";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getVisiblePrice } from "@/utils/currency";

import { HoldersTab } from "../holders-tab/holders-tab";
import { TokenDetailsHeader } from "../token-details-header";
import type { useTokenMetrics } from "../use-token-metrics";

export const LaunchedTokenDetails = ({
  metrics,
}: {
  metrics: ReturnType<typeof useTokenMetrics> | null;
}) => {
  const [activeTab, setActiveTab] = useState<string>("overview");

  useEffect(() => {
    setActiveTab("overview");
  }, [metrics?.selectedPublicKey]);

  if (!metrics) {
    return null;
  }

  const {
    selectedPublicKey,
    selectedLaunchedToken,
    tokenDetails,
    tokenSummary,
    focusExchangeRate,
    coinUserName,
    username,
  } = metrics;

  const publicKey = selectedPublicKey;

  if (!publicKey) return null;

  const tokenPriceUsd = tokenDetails?.coinPrice
    ? getVisiblePrice(tokenDetails.coinPrice)
    : 0;

  return (
    <div
      className="
        relative hidden w-5/12 bg-accent p-6
        sm:block
      "
    >
      <div className="sticky top-[10px]">
        <div className="custom-scrollbar h-full max-h-screen overflow-y-auto">
          <TokenDetailsHeader
            {...{
              username,
              publicKey,
              extraData: tokenDetails?.coinProperties.Profile.ExtraData,
            }}
          />

          <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
            <TabsList
              className="
                m-auto mb-4 flex min-w-fit items-center justify-center gap-1
                rounded-full border border-transparent bg-card p-1 text-center
                text-muted-foreground
              "
            >
              <TabsTrigger
                value="overview"
                className="
                  flex gap-2 rounded-full text-xs
                  data-active:bg-background
                  data-active:text-muted-foreground
                  sm:text-sm
                "
              >
                Overview
              </TabsTrigger>
              <TabsTrigger
                value="holders"
                className="
                  flex gap-2 rounded-full text-xs
                  data-active:bg-background
                  data-active:text-muted-foreground
                  sm:text-sm
                "
              >
                Holders
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview">
              <AdvancedTradingViewChartContainer
                publicKey={publicKey}
                username={coinUserName || username}
                containerClassName="h-[400px]"
                chartClassName="h-[400px]"
              />
              <div className="grid w-full grid-cols-1 gap-4">
                <PostLaunchOverviewTokenMetrics
                  {...{
                    metricsLoading: false,
                    coinData: selectedLaunchedToken,
                    tokenPriceUsd,
                    focusExchangeRate,
                    tokenSummary,
                    username: selectedLaunchedToken?.coinUsername ?? "",
                    publicKey,
                  }}
                />
              </div>
            </TabsContent>

            <TabsContent value="holders">
              <HoldersTab publicKey={publicKey} />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};
